import { createAsyncThunk, createSelector, createSlice, PayloadAction, SerializedError } from "@reduxjs/toolkit";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginatedResponse from "_shared/_api/pagination/responses/paginated-response";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { RootState } from "_shared/_stores/redux-store";
import Guid from "_shared/guids/guid";
import _ from "lodash";
import Session from "users/__session/session";
import { WorkAgreementOrderField } from "work/work-agreements/_api/queries/_fields/work-agreement-order-field";
import { WorkAgreementQueryJSON } from "work/work-agreements/_api/queries/_schemas/work-agreement-query-schema";
import WorkAgreementQuery, {
  WorkAgreementFilterParameters,
  WorkAgreementOrderParameters,
} from "work/work-agreements/_api/queries/work-agreement-query";
import BarebonesWorkAgreementInfoAPIResponse from "work/work-agreements/_api/responses/barebones-work-agreement-api-response";
import WorkAgreementAPIService, {
  WorkAgreementNotFoundError,
} from "work/work-agreements/_api/work-agreement-api-service";
import { WorkAgreementJSON } from "work/work-agreements/_schemas/work-agreement-schema";
import WorkAgreement from "work/work-agreements/work-agreement";

type WorkAgreementStoreState = {
  counts: {
    active: {
      isLoading: boolean;
      total: number | undefined;
    };
    archived: {
      isLoading: boolean;
      total: number | undefined;
    };
  };
  byId: {
    entries: Record<string, WorkAgreementJSON | null | undefined>;
    loading: Record<string, boolean>;
    error: Record<string, SerializedError | null>;
  };
  bareBones: {
    entries: Record<string, BarebonesWorkAgreementInfoAPIResponse | undefined>;
    loading: Record<string, boolean>;
  };
  byQuery: {
    entries: Record<string, PaginatedResponse<WorkAgreementJSON>>;
    loading: Record<string, boolean>;
    error: Record<string, SerializedError | null>;
  };
  interfaceQueries: {
    entries: Record<string, WorkAgreementQueryJSON>;
  };
};
const initialState: WorkAgreementStoreState = {
  counts: {
    active: {
      isLoading: false,
      total: undefined,
    },
    archived: {
      isLoading: false,
      total: undefined,
    },
  },
  byId: {
    entries: {},
    loading: {},
    error: {},
  },
  bareBones: {
    entries: {},
    loading: {},
  },
  byQuery: {
    entries: {},
    loading: {},
    error: {},
  },
  interfaceQueries: {
    entries: {},
  },
};

export const loadActiveWorkAgreementCount = createAsyncThunk(
  "proposals/getActiveWorkAgreementCount",
  async ({ session }: { session: Session }, thunkAPI) => {
    const apiService = new WorkAgreementAPIService(session);
    const abortController = new AbortController();
    const onAbort = () => abortController.abort();
    thunkAPI.signal.addEventListener("abort", onAbort);

    try {
      const query = WorkAgreementQuery.create({
        filterParameters: WorkAgreementFilterParameters.create({}),
        orderParameters: WorkAgreementOrderParameters.create({
          orderBy: WorkAgreementOrderField.Start,
          orderDirection: OrderDirection.Descending,
        }),
        paginationParameters: PaginationParameters.create({ pageIndex: 0, pageSize: 0 }),
      });
      const response = await apiService.getWorkAgreementsAsync(query, abortController);
      return response.headers.totalElements;
    } catch (error) {
      console.error(error);
      return thunkAPI.rejectWithValue(error);
    } finally {
      thunkAPI.signal.removeEventListener("abort", onAbort);
    }
  }
);

export const loadArchivedWorkAgreementCount = createAsyncThunk(
  "proposals/getArchivedWorkAgreementCount",
  async ({ session }: { session: Session }, thunkAPI) => {
    const apiService = new WorkAgreementAPIService(session);
    const abortController = new AbortController();
    const onAbort = () => abortController.abort();
    thunkAPI.signal.addEventListener("abort", onAbort);

    try {
      const query = WorkAgreementQuery.create({
        filterParameters: WorkAgreementFilterParameters.create({
          isArchived: true,
        }),
        orderParameters: WorkAgreementOrderParameters.create({
          orderBy: WorkAgreementOrderField.Start,
          orderDirection: OrderDirection.Descending,
        }),
        paginationParameters: PaginationParameters.create({ pageIndex: 0, pageSize: 0 }),
      });
      const response = await apiService.getWorkAgreementsAsync(query, abortController);
      return response.headers.totalElements;
    } catch (error) {
      console.error(error);
      return thunkAPI.rejectWithValue(error);
    } finally {
      thunkAPI.signal.removeEventListener("abort", onAbort);
    }
  }
);

export const populateWorkAgreement = createAsyncThunk(
  "work/getWorkAgreementById",
  async (
    {
      session,
      id,
    }: {
      session: Session;
      id: Guid;
    },
    thunkAPI
  ) => {
    const apiService = new WorkAgreementAPIService(session);
    const abortController = new AbortController();
    const onAbort = () => abortController.abort();
    thunkAPI.signal.addEventListener("abort", onAbort);

    try {
      return await apiService.getWorkAgreementByIdAsync(id, abortController);
    } catch (error) {
      console.error(error);
      return thunkAPI.rejectWithValue(error);
    } finally {
      thunkAPI.signal.removeEventListener("abort", onAbort);
    }
  }
);

export const populateWorkAgreementsByQuery = createAsyncThunk(
  "work/populateWorkAgreementsByQuery",
  async (
    {
      session,
      query,
    }: {
      session: Session;
      query: WorkAgreementQuery;
    },
    thunkAPI
  ) => {
    if (!query) return;

    const apiService = new WorkAgreementAPIService(session);
    const abortController = new AbortController();
    const onAbort = () => abortController.abort();
    thunkAPI.signal.addEventListener("abort", onAbort);

    try {
      return await apiService.getWorkAgreementsAsync(query, abortController);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    } finally {
      thunkAPI.signal.removeEventListener("abort", onAbort);
    }
  }
);

export const populateBarebonesWorkAgreements = createAsyncThunk<
  BarebonesWorkAgreementInfoAPIResponse[],
  {
    session: Session;
    workAgreementIds: Guid[];
  }
>(
  "work/populateBarebonesWorkAgreements",
  async (
    {
      session,
      workAgreementIds,
    }: {
      session: Session;
      workAgreementIds: Guid[];
    },
    thunkAPI
  ) => {
    if (!workAgreementIds.length) return [];

    const store: { workAgreements: WorkAgreementStoreState } = thunkAPI.getState() as {
      workAgreements: WorkAgreementStoreState;
    };
    const missingWorkAgreementsIds = _.uniqWith(
      workAgreementIds.filter((workAgreementId) => !store.workAgreements.bareBones.entries[workAgreementId.value]),
      (a, b) => a.isEqualTo(b)
    );
    if (!missingWorkAgreementsIds.length) return [];

    const apiService = new WorkAgreementAPIService(session);
    const abortController = new AbortController();
    const onAbort = () => abortController.abort();
    thunkAPI.signal.addEventListener("abort", onAbort);

    try {
      return await apiService.getWorkAgreementsByIds(workAgreementIds, abortController);
    } catch (error) {
      console.error(error);
      return thunkAPI.rejectWithValue(error);
    } finally {
      thunkAPI.signal.removeEventListener("abort", onAbort);
    }
  }
);

export const endWorkAgreement = createAsyncThunk(
  "work/endAgreement",
  async ({ session, id }: { session: Session; id: Guid }, thunkAPI) => {
    const apiService = new WorkAgreementAPIService(session);
    try {
      await apiService.endWorkAgreementAsync(id);
    } catch (error) {
      console.error(error);
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const reviseWorkAgreement = createAsyncThunk(
  "work/reviseAgreement",
  async ({ session, workAgreement }: { session: Session; workAgreement: WorkAgreement }) => {
    const apiService = new WorkAgreementAPIService(session);
    return await apiService.reviseWorkAgreementAsync(workAgreement, session);
  }
);

export const quitWorkTeam = createAsyncThunk(
  "work/quitWorkTeam",
  async ({ session, workAgreementId }: { session: Session; workAgreementId: Guid }) => {
    const apiService = new WorkAgreementAPIService(session);
    return await apiService.quitWorkTeamAsync(workAgreementId, session);
  }
);

const workAgreementsSlice = createSlice({
  name: "workAgreements",
  initialState,
  reducers: {
    addWorkAgreement: (state, action: PayloadAction<WorkAgreement>) => {
      const workAgreement = action.payload;
      if (!workAgreement.id) {
        console.error("Work agreement does not have an id");
        return;
      }

      state.byId.entries[workAgreement.id.value] = workAgreement.toJSON();
      state.counts = {
        active: {
          isLoading: false,
          total: undefined,
        },
        archived: {
          isLoading: false,
          total: undefined,
        },
      };
      state.byQuery = {
        entries: {},
        loading: {},
        error: {},
      };
    },
    removeWorkAgreement: (state, action: PayloadAction<Guid>) => {
      state.byId.entries[action.payload.value] = null;
      state.counts = {
        active: {
          isLoading: false,
          total: undefined,
        },
        archived: {
          isLoading: false,
          total: undefined,
        },
      };
      state.byQuery = {
        entries: {},
        loading: {},
        error: {},
      };
    },
    updateWorkAgreementInterfaceQuery: (
      state,
      action: PayloadAction<{
        interfaceName: string;
        query: WorkAgreementQuery;
      }>
    ) => {
      state.interfaceQueries.entries[action.payload.interfaceName] = action.payload.query.toJSON();
    },
    updateAllQueriesLoading: (state, action: PayloadAction<boolean>) => {
      for (const [query] of Object.entries(state.byQuery.loading)) {
        state.byQuery.loading[query] = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(loadActiveWorkAgreementCount.pending, (state) => {
      state.counts.active.isLoading = true;
    });
    builder.addCase(loadActiveWorkAgreementCount.fulfilled, (state, action) => {
      if (action.payload === undefined || action.payload === null) {
        return;
      }
      state.counts.active.total = action.payload;
      state.counts.active.isLoading = false;
    });
    builder.addCase(loadArchivedWorkAgreementCount.pending, (state) => {
      state.counts.archived.isLoading = true;
    });
    builder.addCase(loadArchivedWorkAgreementCount.fulfilled, (state, action) => {
      if (action.payload === undefined || action.payload === null) {
        return;
      }
      state.counts.archived.total = action.payload;
      state.counts.archived.isLoading = false;
    });
    builder.addCase(populateWorkAgreement.pending, (state, action) => {
      const workAgreementId = action.meta.arg.id.value;
      state.byId.loading[workAgreementId] = true;
      state.byId.error[workAgreementId] = null;
    });
    builder.addCase(populateWorkAgreement.fulfilled, (state, action) => {
      const workAgreement = action.payload;
      if (!workAgreement.id) {
        console.error("Work agreement does not have an id", workAgreement);
        return;
      }
      state.byId.entries[workAgreement.id] = workAgreement;
      state.byQuery = {
        entries: {},
        loading: {},
        error: {},
      };
      state.byId.loading[workAgreement.id] = false;
      state.byId.error[workAgreement.id] = null;
    });
    builder.addCase(populateWorkAgreement.rejected, (state, action) => {
      const workAgreementId = action.meta.arg.id.value;
      state.byId.loading[workAgreementId] = false;
      state.byId.error[workAgreementId] = action.error;
      if (action.payload instanceof WorkAgreementNotFoundError) {
        state.byId.entries[workAgreementId] = null;
      }
    });

    builder.addCase(populateWorkAgreementsByQuery.pending, (state, action) => {
      if (!action.meta.arg.query) {
        return;
      }
      const query = action.meta.arg.query.cacheKey;
      state.byQuery.loading[query] = true;
      state.byQuery.error[query] = null;
    });
    builder.addCase(populateWorkAgreementsByQuery.fulfilled, (state, action) => {
      if (!action.meta.arg.query) {
        return;
      }
      const query = action.meta.arg.query.cacheKey;
      if (!query || !action.payload) {
        return;
      }
      state.byQuery.loading[query] = false;
      const json = action.payload.data;
      const jsonPaginatedResponse = PaginatedResponse.from(json, action.payload.headers);
      state.byQuery.entries[query] = jsonPaginatedResponse;
    });
    builder.addCase(populateWorkAgreementsByQuery.rejected, (state, action) => {
      if (!action.meta.arg.query) {
        return;
      }
      const query = action.meta.arg.query.cacheKey;
      state.byQuery.loading[query] = false;
      state.byQuery.error[query] = action.error;
    });

    builder.addCase(populateBarebonesWorkAgreements.pending, (state, action) => {
      if (!action.meta.arg.workAgreementIds?.length) return;
      for (const workAgreementId of action.meta.arg.workAgreementIds) {
        state.bareBones.loading[workAgreementId.value] = true;
      }
    });
    builder.addCase(populateBarebonesWorkAgreements.fulfilled, (state, action) => {
      for (const workAgreement of action.payload) {
        if (!workAgreement.id) {
          console.error("Work agreement does not have an id", workAgreement);
          continue;
        }
        state.bareBones.entries[workAgreement.id] = workAgreement;
        state.bareBones.loading[workAgreement.id] = false;
      }
    });
    builder.addCase(populateBarebonesWorkAgreements.rejected, (state, action) => {
      for (const workAgreementId of action.meta.arg.workAgreementIds) {
        state.bareBones.loading[workAgreementId.value] = false;
      }
    });
    builder.addCase(endWorkAgreement.pending, (state, action) => {
      const workAgreementId = action.meta.arg.id.value;
      state.byId.loading[workAgreementId] = true;
      state.byId.error[workAgreementId] = null;
    });
    builder.addCase(endWorkAgreement.fulfilled, (state, action) => {
      const workAgreementId = action.meta.arg.id.value;
      state.byId.loading[workAgreementId] = false;
      state.byId.error[workAgreementId] = null;
      state.byId.entries[workAgreementId] = null;
    });
    builder.addCase(endWorkAgreement.rejected, (state, action) => {
      const workAgreementId = action.meta.arg.id.value;
      state.byId.loading[workAgreementId] = false;
      state.byId.error[workAgreementId] = action.error;
    });
    builder.addCase(reviseWorkAgreement.pending, (state, action) => {
      const workAgreementId = action.meta.arg.workAgreement.id?.value;
      if (!workAgreementId) return;
      state.byId.loading[workAgreementId] = true;
      state.byId.error[workAgreementId] = null;
    });
    builder.addCase(reviseWorkAgreement.fulfilled, (state, action) => {
      const newProposal = action.payload;
      if (!newProposal.id) {
        console.error("Revised work agreement does not have an id", newProposal);
        throw new Error("Revised work agreement does not have an id");
      }
      if (!newProposal.workAgreementId) {
        console.error("Revised work agreement does not have an id", newProposal);
        throw new Error("Revised work agreement does not have an id");
      }
      state.byId.entries[newProposal.workAgreementId] = newProposal;
      state.byQuery = {
        entries: {},
        loading: {},
        error: {},
      };
      state.byId.loading[newProposal.id] = false;
      state.byId.error[newProposal.id] = null;
    });
  },
});

export const { addWorkAgreement, removeWorkAgreement, updateWorkAgreementInterfaceQuery, updateAllQueriesLoading } =
  workAgreementsSlice.actions;

const selectWorkAgreementById = createSelector(
  [(state: RootState) => state.workAgreements.byId.entries, (_: RootState, id?: Guid) => id],
  (entries, id) => {
    if (!id) return undefined;
    const entry = entries[id.value];
    if (!entry) return undefined;
    return WorkAgreement.fromJSON(entry);
  }
);
export const getWorkAgreementById = (id: Guid | undefined) => (state: RootState) => selectWorkAgreementById(state, id);

export const getIsLoadingWorkAgreementById = (id?: Guid) => (state: RootState) => {
  if (!id) return false;
  return state.workAgreements?.byId.loading[id.value] || state.workAgreements?.bareBones.loading[id.value];
};
export const getErrorLoadingWorkAgreementById = (id?: Guid) => (state: RootState) => {
  if (!id) return undefined;
  return state.workAgreements?.byId.error[id.value] || state.workAgreements?.bareBones.entries[id.value]?.error;
};
const selectWorkAgreementQueryByInterface = createSelector(
  [
    (state: RootState) => state.workAgreements.interfaceQueries.entries,
    (_: RootState, interfaceName: string) => interfaceName,
  ],
  (entries, interfaceName) => {
    const queryJSON = entries[interfaceName];
    if (!queryJSON) return;
    return WorkAgreementQuery.fromJSON(queryJSON) ?? undefined;
  }
);
export const getWorkAgreementQueryByInterface = (interfaceName: string) => (state: RootState) =>
  selectWorkAgreementQueryByInterface(state, interfaceName);

const selectWorkAgreementsByQuery = createSelector(
  [
    (state: RootState) => state.workAgreements.byQuery.entries,
    (_: RootState, query?: WorkAgreementQuery) => query?.cacheKey,
  ],
  (entries, queryKey) => {
    if (!queryKey) return;
    const paginatedJSON = entries[queryKey];
    if (!paginatedJSON) return;
    const data = paginatedJSON.data.map((json) => WorkAgreement.fromJSON(json));
    return PaginatedResponse.from<WorkAgreement>(data, paginatedJSON.headers);
  }
);
export const getWorkAgreementsByQuery = (query?: WorkAgreementQuery) => (state: RootState) =>
  selectWorkAgreementsByQuery(state, query);

export const getIsLoadingWorkAgreementsByQuery = (query?: WorkAgreementQuery) => (state: RootState) => {
  if (!query) {
    return false;
  }
  const cacheKey = query.cacheKey;
  return state.workAgreements?.byQuery.loading[cacheKey];
};
export const getErrorLoadingWorkAgreementsByQuery = (query?: WorkAgreementQuery) => (state: RootState) => {
  if (!query) {
    return false;
  }
  const cacheKey = query.cacheKey;
  return state.workAgreements?.byQuery.error[cacheKey];
};

const selectWorkAgreementsByIds = createSelector(
  [(state: RootState) => state.workAgreements.bareBones.entries, (_: RootState, ids: Guid[]) => ids],
  (entries, ids) => {
    return ids.map((id) => entries[id.value]).filter((workAgreement) => workAgreement !== undefined);
  }
);
export const getWorkAgreementsByIds = (ids: Guid[]) => (state: RootState) => selectWorkAgreementsByIds(state, ids);

const selectLoadingWorkAgreementIds = createSelector([(state: RootState) => state.workAgreements], (workAgreements) => {
  const detailedIds = Object.keys(workAgreements.byId)
    .filter((workId) => workAgreements.byId.loading[workId])
    .map((workId) => Guid.fromJSON(workId) as Guid);
  const bareBonesIds = Object.keys(workAgreements.bareBones.loading)
    .filter((workId) => workAgreements.bareBones.loading[workId])
    .map((workId) => Guid.fromJSON(workId) as Guid);
  return [...detailedIds, ...bareBonesIds];
});
export const getLoadingWorkAgreementIds = (state: RootState) => selectLoadingWorkAgreementIds(state);

export type WorkAgreementError = {
  id: Guid;
  error: SerializedError;
};
export const getActiveWorkAgreementCount = (state: RootState) => state.workAgreements.counts.active.total;
export const getIsLoadingActiveWorkAgreementCount = (state: RootState) => state.workAgreements.counts.active.isLoading;
export const getArchivedWorkAgreementCount = (state: RootState) => state.workAgreements.counts.archived.total;
export const getIsLoadingArchivedWorkAgreementCount = (state: RootState) =>
  state.workAgreements.counts.archived.isLoading;

export const getAreErrorsLoadingWorkAgreementsByIds = (ids: Guid[]) => (state: RootState) =>
  ids.some(
    (id) => state.workAgreements.byId.error[id.value] || state.workAgreements.bareBones.entries[id.value]?.error
  );

const selectAllWorkAgreementErrors = createSelector([(state: RootState) => state.workAgreements], (workAgreements) => {
  const detailedErrors = Object.keys(workAgreements.byId.error)
    .filter((workId) => workId && workAgreements.byId.error[workId])
    .map((workId) => {
      const error = workAgreements.byId.error[workId]?.message;
      if (!error) return null;
      const id = Guid.fromJSON(workId);
      if (!id) return null;
      return { id, error };
    })
    .filter((record) => record?.id) as WorkAgreementError[];
  const bareBonesErrors = Object.keys(workAgreements.bareBones.entries)
    .map((workId) => {
      const error = workAgreements.bareBones.entries[workId]?.error;
      if (!error) return null;
      const id = Guid.fromJSON(workId);
      return { id, error };
    })
    .filter((record) => record?.id) as WorkAgreementError[];
  return [...detailedErrors, ...bareBonesErrors];
});
export const getAllWorkAgreementErrors = (state: RootState) => selectAllWorkAgreementErrors(state);

export default workAgreementsSlice;
